import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as VoterCardIcon } from '../../Assets/icons/ic_voter_id.svg';
import { ReactComponent as PassportIcon } from '../../Assets/icons/ic_passport.svg';
import { ReactComponent as ChevronRight } from '../../Assets/icons/ic_chevron_right.svg';

import Constants, { DOCUMENT_TYPES } from '../../../Utils/Constants';
import DI from '../../../Utils/DI';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import PersonalDocumentsScreen from './Style';
import IDocumentManager from '../../../Business/IDocumentManager';
import useGaEventTracker from '../../Hooks/useGaEventTracker';
import IAnalyticsManager from '../../../Business/IAnalyticsManager';
import { ISteps } from '../../../Utils/Constants/FormSteps';
import ISessionManager from '../../../Business/ISessionManager';
import useStepContext from '../../Hooks/useStepContext';
import UserManager from '../../../Business/UserManager/UserManager';
import { IDocument } from '../../../Service/IUserService';

const PersonalDocuments: FC<IFormStepProps> = ({ nextStepHandler, setWhitelistedUserCallback }) => {
    const {
        currentFormStep,
        setCurrentFormStep,
        whitelistedUser,
        setWhitelistedUser,
        setDocumentType,
        documentType,
        setDocumentDescription,
        documentDescription,
        kycDetails,
        setKycDetails,
        setCaptureBackSide
    } = useStepContext();
    const userManager = UserManager.getInstance();
    const [identityDocuments, setIdentityDocuments] = useState<IDocument[]>([]);
    useEffect(() => {
        const isWhiteListed = userManager.getWhiteListedMsisdn();
        setWhitelistedUser(isWhiteListed);
        if (setWhitelistedUserCallback) {
            setWhitelistedUserCallback(isWhiteListed);
        }

        const docs = userManager.getIdentityDocuments();
        if (docs && docs.length) {
            setIdentityDocuments(docs);
        }
    }, [setWhitelistedUserCallback, setWhitelistedUser]);
    const { t, i18n } = useTranslation();
    const { screenTracker } = useGaEventTracker();
    const analyticsManager: IAnalyticsManager = DI.get('AnalyticsManager');
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const getIconForDocumentCode = (code: string) => {
        switch (code) {
        case DOCUMENT_TYPES.VOTER_CARD_1:
        case DOCUMENT_TYPES.VOTER_CARD_2:
            return <VoterCardIcon />;
        case DOCUMENT_TYPES.PASSPORT:
            return <PassportIcon />;
        default:
            return <VoterCardIcon />;
        }
    };

    const documents = [
        ...identityDocuments
            .filter((doc) => doc.code !== DOCUMENT_TYPES.OTHERS)
            .map((doc) => ({
                icon: getIconForDocumentCode(doc.code),
                description: i18n.language.toLowerCase() === 'fr' && doc.fr ? doc.fr : doc.en,
                code: doc.code,
                en: doc.en,
                fr: doc.fr,
                documentId: doc.documentId,
                captureBackSide: doc.captureBackSide,
                documentType: doc.documentType

            })),
        ...(whitelistedUser
            ? [{
                icon: <VoterCardIcon />,
                description: DOCUMENT_TYPES.OTHERS,
                code: DOCUMENT_TYPES.OTHERS,
                en: DOCUMENT_TYPES.OTHERS,
                fr: DOCUMENT_TYPES.OTHERS,
                documentId: 999,
                captureBackSide: false,
                documentType: DOCUMENT_TYPES.OTHERS
            }]
            : [])
    ];
    const selectDocumentHandler = (document: string, whitelistedUser: boolean) => {
        const selectedDoc = documents.find((d) => d.description === document);
        if (!selectedDoc) return;

        const documentManager: IDocumentManager = DI.get('DocumentManager');
        const cleaned = selectedDoc.documentType;

        if (cleaned === DOCUMENT_TYPES.OTHERS) {
            setDocumentType(DOCUMENT_TYPES.OTHERS);
            documentManager.setDocumentType(DOCUMENT_TYPES.OTHERS);
            documentManager.processOtherDocumentType(whitelistedUser, DOCUMENT_TYPES.OTHERS);
            setDocumentDescription(selectedDoc.description);
        } else {
            setDocumentType(cleaned);
            documentManager.setDocumentType(cleaned);
            documentManager.processOtherDocumentType(whitelistedUser, cleaned);
            setDocumentDescription(selectedDoc.description);
        }

        screenTracker({ screen: '4.OCR', screenClass: document });
        analyticsManager.setTemporarilySelectedDocument(document);

        const selectedDocument = whitelistedUser
            ? 'METADATA_OTHERS' as keyof ISteps
            : 'STANDARD_INSTRUCTIONS_DETAILS' as keyof ISteps;

        setCaptureBackSide(Boolean(selectedDoc.captureBackSide));

        nextStepHandler({
            ...Constants.FORM_STEPS[selectedDocument],
            PROPS: {
                selectedDocumentId: selectedDoc.documentId,
                selectedDocumentEn: selectedDoc.en,
                selectedDocumentFr: selectedDoc.fr,
                captureBackSide: selectedDoc.captureBackSide,
                documentType: cleaned
            }
        });
    };
    useEffect(() => {
        const documentManager: IDocumentManager = DI.get('DocumentManager');
        documentManager.clearProcessedDocuments();
    }, []);

    return (
        <>
            <PersonalDocumentsScreen>
                <div className="wrapper">
                    <div className="title">
                        <span>
                            {sessionManager?.getSession()?.getIsValid()
                                ? t('personalDocuments.selectCustomerDocument')
                                : t('personalDocuments.selectSelfDocument')}
                        </span>
                    </div>
                    <div className="subtitle">
                        <span>
                            {t('personalDocuments.chooseDocument')}
                        </span>
                    </div>
                    <ul className="personal-documents">
                        {documents.map((document) => (
                            <li
                                key={document.description}
                                className="personal-document-item"
                                onClick={() => selectDocumentHandler(document.description, whitelistedUser)}
                                id={`personal-document-${document.description}`}
                            >
                                <div className="personal-document-item__icon">
                                    {document.icon}
                                </div>
                                <div className="personal-document-item__description">
                                    {document.description}
                                </div>
                                <ChevronRight />
                            </li>
                        ))}
                    </ul>
                </div>
            </PersonalDocumentsScreen>
        </>
    );
};
export default PersonalDocuments;
